import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { Target, ArrowRight, Trophy } from "lucide-react";
import {
  GET_SEEN_TEMPLATE,
  GET_SEEN_RECOMMENDED_DAYS,
  getSeenDayRanges,
  getSeenDayDate,
} from "@/constants/getSeenTemplate";

const EMPTY = { answers: { step1: {}, step2: {} }, checklists: { step6: {}, step7: {}, step9: {} } };

const normalize = (gs) => ({
  answers: { step1: { ...(gs?.answers?.step1 || {}) }, step2: { ...(gs?.answers?.step2 || {}) } },
  checklists: {
    step6: { ...(gs?.checklists?.step6 || {}) },
    step7: { ...(gs?.checklists?.step7 || {}) },
    step9: { ...(gs?.checklists?.step9 || {}) },
  },
});

/**
 * BĄDŹ WIDOCZNY / GET SEEN (goal.template === "get_seen").
 * Text fields + checkboxes share ONE state object (dataRef) persisted to
 * goal.template_data.getSeen via the existing PATCH /career/goals/{id}.
 */
export default function GetSeenGoalContent({ goal, lang, onGoalUpdated }) {
  const L = GET_SEEN_TEMPLATE[lang === "pl" ? "pl" : "en"];
  const [data, setData] = useState(EMPTY);
  const [status, setStatus] = useState("idle"); // idle | saving | saved | error
  const dataRef = useRef(EMPTY);
  const otherTdRef = useRef({});
  const debounceRef = useRef(null);
  const seqRef = useRef(0);
  const bootRef = useRef(false);

  useEffect(() => {
    const td = goal?.template_data || {};
    otherTdRef.current = td;
    dataRef.current = normalize(td.getSeen);
    setData(dataRef.current);
    bootRef.current = true;
    /* eslint-disable-next-line */
  }, [goal?.goal_id]);

  useEffect(() => () => debounceRef.current && clearTimeout(debounceRef.current), []);

  const schedulePatch = () => {
    if (!bootRef.current) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const seq = ++seqRef.current;
      setStatus("saving");
      try {
        const r = await api.patch(`/career/goals/${goal.goal_id}`, {
          template_data: { ...otherTdRef.current, getSeen: dataRef.current },
        });
        if (seq !== seqRef.current) return; // stale response — a newer save is in flight
        otherTdRef.current = r.data?.template_data || otherTdRef.current;
        setStatus("saved");
        onGoalUpdated?.(r.data);
      } catch {
        if (seq === seqRef.current) setStatus("error");
      }
    }, 700);
  };

  const update = (mutate) => {
    const next = normalize(dataRef.current);
    mutate(next);
    dataRef.current = next;
    setData(next);
    schedulePatch();
  };

  const onField = (stepKey, key, value) => update((n) => { n.answers[stepKey][key] = value; });
  const onToggle = (listKey, i) => update((n) => { n.checklists[listKey][i] = !n.checklists[listKey][i]; });

  const days = parseInt(goal?.duration_days, 10) || GET_SEEN_RECOMMENDED_DAYS;
  const ranges = getSeenDayRanges(days);
  const start = goal?.created_at ? new Date(goal.created_at) : new Date();
  const fmtDate = (d) => d.toLocaleDateString(lang === "pl" ? "pl-PL" : "en-US", { day: "numeric", month: "short", year: "numeric" });
  const steps = (goal?.steps || []).filter((s) => s && typeof s === "object");
  const completed = steps.length > 0 && (steps[9]?.done || steps.every((s) => s.done));

  return (
    <div className="mt-2" data-testid="get-seen-goal-content">
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <div className="mono text-[11px] tracking-[0.35em] text-amber-500 uppercase flex items-center gap-1">
          <Target size={11} /> {L.eyebrow}
        </div>
        {status !== "idle" && (
          <span
            data-testid="get-seen-save-status"
            className={`mono text-[10px] uppercase tracking-widest ${status === "error" ? "text-red-400" : "text-stone-500"}`}
          >
            {status === "saving" ? L.savingLabel : status === "saved" ? L.savedLabel : L.saveErrorLabel}
          </span>
        )}
      </div>

      <div className="paper p-6 border-amber-500/30 bg-amber-500/5">
        <h2 className="serif text-2xl sm:text-3xl text-stone-100">{L.subtitle}</h2>
        <div className="mono text-[11px] tracking-[0.2em] text-stone-400 uppercase mt-2" data-testid="get-seen-pace">
          {L.paceLabel} · {L.yourPaceLabel}: {days} {L.daysWord} ({L.recommendedNote})
        </div>
        <div className="label-cap mt-5">{L.aboutLabel}</div>
        <p className="text-stone-200 text-sm leading-relaxed whitespace-pre-line mt-2">{L.intro}</p>
        <p className="text-amber-100 text-sm leading-relaxed mt-4">
          {days === GET_SEEN_RECOMMENDED_DAYS ? L.goal : L.goalFlexible.replace("{days}", days)}
        </p>
        <div className="label-cap mt-5">{L.accomplishTitle}</div>
        <ul className="mt-2 space-y-1 text-stone-300 text-sm list-disc pl-5">
          {L.accomplish.map((a, i) => <li key={i}>{a}</li>)}
        </ul>
      </div>

      <div className="space-y-12 mt-10">
        {L.steps.map((step, idx) => {
          const [ds, de] = ranges[idx];
          const stepKey = `step${idx + 1}`;
          return (
            <motion.section
              key={idx}
              data-testid={`get-seen-step-${idx + 1}`}
              initial={{ opacity: 0, y: 6 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.05 }}
              transition={{ duration: 0.3 }}
            >
              <div className="mono text-[11px] tracking-[0.35em] text-amber-500 uppercase">
                {L.stepLabel} {String(idx + 1).padStart(2, "0")}
              </div>
              <h2 className="serif text-2xl sm:text-3xl text-stone-100 mt-1">{step.title}</h2>
              <div className="mono text-[11px] tracking-widest text-stone-400 uppercase mt-2" data-testid={`get-seen-step-${idx + 1}-days`}>
                {ds === de ? `${L.dayLabel} ${ds}` : `${L.daysLabel} ${ds}–${de}`} · {L.dueLabel} {fmtDate(getSeenDayDate(start, de))}
              </div>

              <div className="label-cap mt-5">{L.whyLabel}</div>
              <div className="text-stone-300 leading-relaxed mt-2 whitespace-pre-line">{step.why}</div>

              <div className="label-cap mt-5">{L.taskLabel}</div>
              <div className="text-stone-200 leading-relaxed mt-2 whitespace-pre-line">{step.task}</div>

              {step.fields && (
                <ul className="mt-4 space-y-3">
                  {step.fields.map((f) => (
                    <li key={f.key} className="paper p-4">
                      <label className="serif text-base text-stone-100 block">{f.label}</label>
                      <textarea
                        data-testid={`get-seen-field-${stepKey}-${f.key}`}
                        value={data.answers[stepKey]?.[f.key] || ""}
                        onChange={(e) => onField(stepKey, f.key, e.target.value)}
                        rows={2}
                        placeholder={L.answerPlaceholder}
                        className="script-input mono text-sm mt-3 w-full"
                      />
                    </li>
                  ))}
                </ul>
              )}

              {step.checklist && (
                <ul className="mt-4 space-y-2">
                  {step.checklist.items.map((label, i) => {
                    const checked = !!data.checklists[step.checklist.key]?.[i];
                    return (
                      <li key={i}>
                        <label className="paper p-3 flex items-start gap-3 cursor-pointer hover:border-amber-500/50">
                          <input
                            type="checkbox"
                            data-testid={`get-seen-check-${step.checklist.key}-${i}`}
                            checked={checked}
                            onChange={() => onToggle(step.checklist.key, i)}
                            className="w-4 h-4 accent-amber-500 mt-0.5"
                          />
                          <span className={`text-sm ${checked ? "text-stone-500 line-through" : "text-stone-200"}`}>{label}</span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}

              {step.example && (
                <div className="mt-5 paper p-4">
                  <div className="label-cap">{L.exampleLabel}</div>
                  <p className="serif italic text-stone-200 leading-relaxed whitespace-pre-line mt-2">{step.example}</p>
                </div>
              )}

              {step.after && (
                <div className="text-stone-300 text-sm leading-relaxed mt-5 whitespace-pre-line">{step.after}</div>
              )}

              <div className="mt-5 paper p-4 border-amber-500/30 bg-amber-500/5 text-amber-100 text-sm">
                <span className="mono text-[10px] uppercase tracking-widest text-amber-500 mr-2">{L.resultLabel}:</span>
                {step.result}
              </div>

              {step.link && (
                <div className="mt-4">
                  <Link to={step.link.to} data-testid={`get-seen-link-${idx + 1}`} className="pill-btn !py-2 !text-xs inline-flex">
                    {step.link.label} <ArrowRight size={12} />
                  </Link>
                </div>
              )}
            </motion.section>
          );
        })}
      </div>

      {completed && (
        <motion.div
          data-testid="get-seen-completion"
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="paper p-6 mt-12 border-amber-500/40 bg-amber-500/5"
        >
          <div className="flex items-center gap-2 text-amber-500"><Trophy size={16} /></div>
          <h2 className="serif text-2xl sm:text-3xl text-stone-100 mt-2">{L.completion.title}</h2>
          <p className="text-stone-300 text-sm mt-3">{L.completion.lead}</p>
          <ul className="mt-2 space-y-1 text-stone-200 text-sm list-disc pl-5">
            {L.completion.items.map((it, i) => <li key={i}>{it}</li>)}
          </ul>
          <p className="text-stone-200 leading-relaxed mt-4">{L.completion.closing}</p>
          <div className="label-cap mt-5">{L.completion.nextTitle}</div>
          <p className="text-stone-300 text-sm leading-relaxed whitespace-pre-line mt-2">{L.completion.next}</p>
          <Link to="/career" data-testid="get-seen-repeat" className="pill-btn !py-2 !text-xs inline-flex mt-5">
            {L.completion.repeatCta} <ArrowRight size={12} />
          </Link>
        </motion.div>
      )}
    </div>
  );
}
